import React, { useEffect, useRef, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useLang } from '../util/lang';
import SHADE_IMAGES from './shadeImages';
import './FitShadeSelector.css';

const SHADE_GROUPS = [
    {
        nameKey: '@PatientInfo.smileDesign.restorativePhase.standardShades',
        guides: [
            { nameKey: '@PatientInfo.smileDesign.restorativePhase.gcBleachShadeGuide', shades: ['XBW', 'BW'] },
            { nameKey: '@PatientInfo.smileDesign.restorativePhase.vitaClassicShadeGuide', shades: ['A1', 'A2', 'A3', 'A3.5', 'A4', 'B1', 'B2'] },
        ],
    },
    {
        nameKey: '@PatientInfo.smileDesign.restorativePhase.opaqueShades',
        guides: [{ shades: ['AO1', 'AO2', 'AO3'] }],
    },
    {
        nameKey: '@PatientInfo.smileDesign.restorativePhase.translucentShades',
        guides: [{ shades: ['JE', 'AE'] }],
    },
];

const LANG_PREFIX = '@PatientInfo.smileDesign.restorativePhase';

const LAYERS = {
    enamel: { labelKey: `${LANG_PREFIX}.layerEnamel`, color: '#61CBF4', tint: '#eaf9fe' },
    dentin: { labelKey: `${LANG_PREFIX}.layerDentin`, color: '#D86ECC', tint: '#fdf1fb' },
};

/** Restores the two layer selections from the stored shade array. */
const getInitialLayerSelections = (value) => {
    const selectedShades = Array.isArray(value) ? value : [];
    const enamelShade = selectedShades.find((shade) => shade?.layer === 'enamel')?.option;
    const dentinShade = selectedShades.find((shade) => shade?.layer === 'dentin')?.option;
    return {
        dentin: dentinShade || (typeof selectedShades[0] === 'string' ? selectedShades[0] : ''),
        enamel: enamelShade || (typeof selectedShades[1] === 'string' ? selectedShades[1] : ''),
    };
};

/** Extracts the stored shade option for a single-layer case. */
const getSingleLayerShade = (value) => {
    if (!Array.isArray(value)) return '';
    return value.find((shade) => shade?.layer === 'enamelOrDentin')?.option ||
        (typeof value[0] === 'string' ? value[0] : '');
};

/** Renders FIT shade choices with layer-specific selection rules. */
const FitShadeSelector = ({ name, required = false }) => {
    const get = useLang();
    const { control } = useFormContext();
    const injectionLayers = useWatch({ control, name: 'injectionLayers' });
    const selectedShades = useWatch({ control, name }) || [];
    const [activeLayer, setActiveLayer] = useState(() =>
        injectionLayers === 'twoLayers' ? 'enamel' : 'dentin'
    );
    const [layerSelections, setLayerSelections] = useState(() => getInitialLayerSelections(selectedShades));
    const previousInjectionLayers = useRef(injectionLayers);

    /** Resets layer-specific UI state when the injection layer count changes. */
    useEffect(() => {
        if (previousInjectionLayers.current !== injectionLayers) {
            setActiveLayer(injectionLayers === 'twoLayers' ? 'enamel' : 'dentin');
            setLayerSelections({ dentin: '', enamel: '' });
        }
        previousInjectionLayers.current = injectionLayers;
    }, [injectionLayers]);

    /** Updates a two-layer selection while preserving one shade per layer. */
    const selectTwoLayerShade = (shade, field) => {
        const nextSelections = {
            ...layerSelections,
            [activeLayer]: layerSelections[activeLayer] === shade ? '' : shade,
        };
        setLayerSelections(nextSelections);
        field.onChange(
            [
                nextSelections.enamel && { layer: 'enamel', option: nextSelections.enamel },
                nextSelections.dentin && { layer: 'dentin', option: nextSelections.dentin },
            ].filter(Boolean)
        );
    };

    /** Selects or clears the only available shade for a single-layer case. */
    const selectSingleLayerShade = (shade, field) => {
        field.onChange(
            getSingleLayerShade(selectedShades) === shade
                ? []
                : [{ layer: 'enamelOrDentin', option: shade }]
        );
    };

    return (
        <Controller
            name={name}
            control={control}
            defaultValue={[]}
            rules={
                required
                    ? {
                          validate: (value) => {
                              if (!Array.isArray(value)) return 'Required';
                              if (injectionLayers === 'twoLayers') {
                                  const layers = value.map((shade) => shade?.layer);
                                  return value.length === 2 && layers.includes('enamel') && layers.includes('dentin')
                                      ? true
                                      : 'Required';
                              }
                              return value.length === 1 && value.some((shade) => shade?.layer === 'enamelOrDentin' && shade?.option)
                                  ? true
                                  : 'Required';
                          },
                      }
                    : undefined
            }
            render={({ field }) => {
                const isTwoLayers = injectionLayers === 'twoLayers';
                const isSingleLayer = injectionLayers === 'oneLayer';
                const singleLayerShade = getSingleLayerShade(selectedShades);

                return (
                    <div className="fit-shade-selector">
                        <div className="restorative-hint fit-shade-selector__remark">{get(`${LANG_PREFIX}.eachLayerOneSyringe`)}</div>
                        <div className="fit-shade-selector__content">
                            <div className={`fit-shade-selector__groups${isTwoLayers ? ' fit-shade-selector__groups--two-layers' : ''}`}>
                                {SHADE_GROUPS.map((group) => (
                                    <section className="fit-shade-selector__group" key={group.nameKey}>
                                        <h4>{get(group.nameKey)}</h4>
                                        <div className="fit-shade-selector__guides">
                                            {group.guides.map((guide) => (
                                                <div className="fit-shade-selector__guide" key={guide.nameKey || group.nameKey}>
                                                    {guide.nameKey && <div className="fit-shade-selector__guide-title">{get(guide.nameKey)}</div>}
                                                    <div className="fit-shade-selector__options">
                                                        {guide.shades.map((shade) => {
                                                            const selectedLayers = isTwoLayers
                                                                ? Object.keys(LAYERS).filter((layer) => layerSelections[layer] === shade)
                                                                    : singleLayerShade === shade
                                                                    ? ['single']
                                                                    : [];
                                                            const selectionClassName = selectedLayers
                                                                .map((layer) => ` fit-shade-selector__option--${layer}`)
                                                                .join('');

                                                            return (
                                                                <button
                                                                    key={shade}
                                                                    type="button"
                                                                    className={`fit-shade-selector__option${selectionClassName}`}
                                                                    onClick={() => {
                                                                        if (isTwoLayers) selectTwoLayerShade(shade, field);
                                                                        if (isSingleLayer) selectSingleLayerShade(shade, field);
                                                                    }}
                                                                >
                                                                    <img src={SHADE_IMAGES[shade]} alt={shade} />
                                                                    <span>{shade}</span>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </section>
                                ))}
                            </div>
                            {isTwoLayers && (
                                <div className="fit-shade-selector__layers" aria-label={get(`${LANG_PREFIX}.selectActiveShadeLayer`)}>
                                    {Object.entries(LAYERS).map(([layer, config]) => (
                                        <button
                                            key={layer}
                                            type="button"
                                            className={`fit-shade-selector__layer${activeLayer === layer ? ' fit-shade-selector__layer--active' : ''}`}
                                            style={{ '--layer-color': config.color, '--layer-tint': config.tint }}
                                            aria-pressed={activeLayer === layer}
                                            onClick={() => setActiveLayer(layer)}
                                        >
                                            <span className="fit-shade-selector__layer-swatch" style={{ backgroundColor: config.color }} />
                                            {get(config.labelKey)}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                );
            }}
        />
    );
};

export default FitShadeSelector;
